import { getSystem } from "../../data/loadSystems";
import { loadShips } from "../../data/loadShips";
import { startingFuelCapacity } from "../../fuel";
import { startingProvisionsCapacity } from "../../provisions";
import { effectiveNow } from "../../gameClock";
import {
  describeDock,
  parseDockKey,
  POD_IN_LOCATION_MPS,
  shipsStoredInSystem,
  STATION_LOCATION_ID
} from "../../shipStorage";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";

// Escape Pod (design doc 16.62, Don's direction) -- where an ejected
// captain (mutiny, or otherwise shipless-and-podded) ends up. A minimal,
// non-combat mode: travel only via Warp Gates (system to system, no fuel
// pool of its own -- a pod is an emergency vessel, not a real ship), then
// either pick up a Loaner Ship (16.63) at a Station or reclaim an owned
// ship wherever it's docked (FleetShip.dockedAtBaseKey).
// Fixed loaner hull: a mid-tier Multi-role Standard ship from the real
// roster (16.16) -- reused directly rather than inventing a new ship,
// same "no new stat blocks" convention the rest of the roster follows.
function loanerShip() {
  return loadShips().find((s) => s.role === "Multi-role" && s.hullTier === "T2" && s.variant === "Standard") ?? loadShips()[0];
}

export function renderEscapePodScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    const system = getSystem(state.currentSystemId)!;
    const connections = system.connections.map((id) => getSystem(id)).filter((s): s is NonNullable<typeof s> => !!s);
    // Owned hulls stored anywhere in this System -- Station or Base
    // alike (16.72). The pod arrives in a System rather than at one
    // specific Location, so this deliberately isn't Location-scoped.
    const dockedHere = shipsStoredInSystem(state.fleet, state.currentShipInstanceId, system.id);

    wrap.innerHTML = `
      <h1>Escape Pod</h1>
      <p class="dim">
        A small, unarmed pod -- no cargo, no weapons, no fuel pool of its own. Travel system to
        system via Warp Gates, then either pick up a Loaner Ship at a Station (Faction Missions
        only, 30% reward) or board one of your own ships wherever you left it.
      </p>
      <p class="dim">
        <strong>Nothing can attack you in the pod</strong>, and it moves 50% faster than the
        fastest hull in the game (${Math.round(POD_IN_LOCATION_MPS)} m/s) -- crossing the galaxy to
        reach a parked ship is meant to be safe and quick, not another gauntlet.
      </p>
      <p class="dim"><strong>Current system:</strong> ${system.name} &middot; ${state.credits.toLocaleString()} CR</p>

      ${
        dockedHere.length > 0
          ? `<h2>Board a ship stored in this system</h2>
             <div class="cargo-list">
               ${dockedHere
                 .map(
                   (f) => `
                 <div class="cargo-row">
                   <label><span class="cargo-row-name">${f.ship.name} (${f.ship.variant})</span></label>
                   <span class="dim">${describeDock(f.dockedAtKey, (id) => getSystem(id)?.name)}</span>
                   <button type="button" data-reclaim="${f.id}">Board</button>
                 </div>
               `
                 )
                 .join("")}
             </div>`
          : ""
      }

      ${
        system.hasStation
          ? `<h2>Station</h2>
             <div class="cargo-list">
               <div class="cargo-row">
                 <span class="dim">Pick up a Loaner Ship -- Faction Missions only, 30% of normal reward, can't be sold or have its cargo managed.</span>
                 <button type="button" data-loaner>Pick Up Loaner Ship</button>
               </div>
             </div>`
          : ""
      }

      <h2>Warp to a connected system</h2>
      <div class="cargo-list">
        ${connections
          .map(
            (s) => `
          <div class="cargo-row">
            <label><span class="cargo-row-name">${s.name}</span></label>
            <button type="button" data-warp="${s.id}">Warp</button>
          </div>
        `
          )
          .join("")}
      </div>
    `;

    wrap.querySelectorAll<HTMLButtonElement>("button[data-warp]").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.currentSystemId = btn.dataset.warp!;
        triggerSave(ctx);
        render();
      });
    });

    wrap.querySelector<HTMLButtonElement>("button[data-loaner]")?.addEventListener("click", () => {
      pickUpLoaner();
    });

    wrap.querySelectorAll<HTMLButtonElement>("button[data-reclaim]").forEach((btn) => {
      btn.addEventListener("click", () => reclaimShip(btn.dataset.reclaim!));
    });
  }

  function pickUpLoaner(): void {
    const ship = loanerShip();
    state.currentShip = ship;
    state.currentShipInstanceId = `loaner-${crypto.randomUUID()}`;
    state.isLoanerShip = true;
    state.inEscapePod = false;
    state.fuelCapacity = startingFuelCapacity(ship.hullTierLabel);
    state.fuelOnBoard = state.fuelCapacity;
    state.provisionsCapacity = startingProvisionsCapacity(ship.crew);
    state.provisionsOnBoard = state.provisionsCapacity;
    state.provisionsLastUpdatedAt = effectiveNow(state.clock, Date.now());
    state.lastTopLevelView = "station";
    state.lastEvent = `Picked up a Loaner Ship (${ship.name}) -- Faction Missions only, 30% reward.`;
    triggerSave(ctx);
    ctx.navigate("board");
  }

  function reclaimShip(fleetId: string): void {
    const f = state.fleet.find((x) => x.id === fleetId);
    if (!f) return;
    // Boarding lands the captain at wherever this hull was stored, not
    // wherever the pod happened to be floating (16.72).
    const parsed = f.dockedAtKey ? parseDockKey(f.dockedAtKey) : null;
    if (parsed) {
      state.currentSystemId = parsed.systemId;
      state.currentLocationId = parsed.locationId;
      state.lastTopLevelView = parsed.locationId === STATION_LOCATION_ID ? "station" : "base";
    }
    f.dockedAtKey = undefined; // it's with the captain now, not in storage
    state.currentShip = f.ship;
    state.currentShipInstanceId = f.id;
    state.isLoanerShip = false;
    state.inEscapePod = false;
    state.fuelCapacity = startingFuelCapacity(f.ship.hullTierLabel);
    state.fuelOnBoard = state.fuelCapacity;
    state.provisionsCapacity = startingProvisionsCapacity(f.ship.crew);
    state.provisionsOnBoard = state.provisionsCapacity;
    state.provisionsLastUpdatedAt = effectiveNow(state.clock, Date.now());
    // Land where the ship actually was -- Station or Base. This used to
    // hardcode "base", which sent a captain boarding at a Station to a
    // Base View they may not even own (and which 16.76 now bounces).
    const destination = state.lastTopLevelView === "station" ? "station" : "base";
    state.lastEvent = `Boarded ${f.ship.name} (${f.ship.variant}).`;
    triggerSave(ctx);
    ctx.navigate(destination);
  }

  render();
}
